// ============================================
// RÉPONSES TYPES — chargées depuis la base
// ============================================
// GET /admin/canned-responses rend `{ responses: [{ id, title, content, tags }] }`.
// Variables reconnues : {{ticketRef}}, {{customerName}}, {{agentName}}.
// Taper « / » en début de ligne dans la zone de saisie ouvre la liste,
// filtrée par ce qui suit la barre.

import type { CannedResponse, Ticket, User } from '../../types';

export type ReponseType = CannedResponse;

/** Accepte `{ responses }`, `{ data }` ou un tableau ; ignore les entrées incomplètes. */
export function normaliserReponsesTypes(brut: unknown): ReponseType[] {
  const liste: unknown = Array.isArray(brut)
    ? brut
    : brut && typeof brut === 'object'
      ? ((brut as Record<string, unknown>).responses ?? (brut as Record<string, unknown>).data)
      : null;
  if (!Array.isArray(liste)) return [];
  return liste
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .filter(r => typeof r.id === 'string' && typeof r.title === 'string' && typeof r.content === 'string')
    .map(r => ({
      id: r.id as string,
      title: r.title as string,
      content: r.content as string,
      tags: Array.isArray(r.tags) ? (r.tags as unknown[]).filter((t): t is string => typeof t === 'string') : [],
    }));
}

export interface ContexteVariables {
  ticketRef: string;
  customerName: string;
  agentName: string;
}

/** Valeurs des variables pour un dossier et l'agent connecté, avec des replis lisibles. */
export function contexteDepuisDossier(ticket: Partial<Ticket> | undefined, agent: Partial<User> | null | undefined): ContexteVariables {
  return {
    ticketRef: ticket?.ticketRef || (ticket?.ticketNumber != null ? String(ticket.ticketNumber) : ''),
    customerName: ticket?.customer?.displayName || ticket?.contactName || ticket?.companyName || 'Madame, Monsieur',
    agentName: agent?.displayName || "L'équipe SAV KLY",
  };
}

/**
 * Remplace {{ticketRef}}, {{customerName}} et {{agentName}} (espaces tolérés
 * dans les accolades). Une variable inconnue reste telle quelle : l'agent la
 * voit et la complète avant d'envoyer.
 */
export function substituerVariables(contenu: string, contexte: ContexteVariables): string {
  return contenu.replace(/\{\{\s*([A-Za-z]+)\s*\}\}/g, (tout, nom: string) => {
    if (nom === 'ticketRef' || nom === 'customerName' || nom === 'agentName') return contexte[nom];
    return tout;
  });
}

export interface CommandeSlash {
  /** Début de la ligne (position de la barre). */
  debut: number;
  /** Position du curseur (fin de la commande). */
  fin: number;
  /** Texte tapé après la barre. */
  requete: string;
}

/**
 * Détecte une commande « / » : la ligne du curseur commence par « / » et,
 * jusqu'au curseur, ne contient pas d'espace. « /gar » → requête « gar ».
 * Un « / » au milieu d'une ligne (date, chemin) ne déclenche rien.
 */
export function detecterCommandeSlash(texte: string, curseur: number): CommandeSlash | null {
  const fin = Math.max(0, Math.min(curseur, texte.length));
  const debut = texte.lastIndexOf('\n', fin - 1) + 1;
  const segment = texte.slice(debut, fin);
  const m = /^\/(\S*)$/.exec(segment);
  if (!m) return null;
  return { debut, fin, requete: m[1] };
}

const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Filtre par titre, étiquettes puis contenu ; insensible à la casse et aux accents. Titres en tête. */
export function filtrerReponsesTypes(liste: ReponseType[], requete: string): ReponseType[] {
  const q = sansAccents(requete.trim());
  if (!q) return liste;
  const parTitre: ReponseType[] = [];
  const autres: ReponseType[] = [];
  for (const r of liste) {
    if (sansAccents(r.title).includes(q)) parTitre.push(r);
    else if (r.tags.some(t => sansAccents(t).includes(q)) || sansAccents(r.content).includes(q)) autres.push(r);
  }
  return [...parTitre, ...autres];
}

/** Remplace la commande « /… » par le texte choisi ; rend le texte et la nouvelle position du curseur. */
export function insererReponse(texte: string, commande: CommandeSlash, contenu: string): { texte: string; curseur: number } {
  const avant = texte.slice(0, commande.debut);
  const apres = texte.slice(commande.fin);
  return { texte: avant + contenu + apres, curseur: avant.length + contenu.length };
}
